import { GridRenderCellParams } from "@mui/x-data-grid";
import { messages } from "../../../locales/messages";
import { AllowedTableColumn } from "../../../utils/types/AllowTableTypes";
import { RenderEditButton } from "../../components/RenderEditButtonTable";
import { useTranslation } from "react-i18next";
import {
  FlyerScraperSource,
  GfStoreDto,
} from "@swagger/typescript-fetch-goflyer";
import { useNavigate } from "react-router-dom";
import { makeStoreDisplayName } from "../../../utils/dtoUtil/makeStoreDisplayName";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { routes } from "../../../utils/constant/navigationRoutes";

export const useFlyerScraperTableColumns = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const values: AllowedTableColumn[] = ["updatedAt", "createdAt"];
  /**
   * Handles the click event to navigate to the store editing page with pre-filled data.
   *
   * @param value - The EditProps containing store details for editing.
   */
  const handleOnClickEdit = async (value: GfStoreDto) => {
    navigate(routes.flyerScraper.editById(value?.id || ""));
  };

  const columns = [
    {
      field: "edit",
      width: 90,
      headerName: `${t(messages.Edit())}`,
      filterable: false,
      renderCell: (props: GridRenderCellParams) => {
        return (
          <PermissionComponentWrapper
            requiredPermission={["edit"]}
            requiredPermissionGroup={["flyerScraper"]}
          >
            <RenderEditButton
              onClick={handleOnClickEdit}
              {...props}
            ></RenderEditButton>
          </PermissionComponentWrapper>
        );
      },
      sortable: false,
    },
    {
      field: "merchant",
      width: 250,
      headerName: `${t(messages["Merchant"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<FlyerScraperSource>) => {
        const store = params.row.stores[0];
        return <div>{makeStoreDisplayName(store)}</div>;
      },
    },

    {
      field: "url",
      width: 250,
      headerName: `${t(messages.URL())}`,
    },
    {
      field: "backupUrl",
      width: 250,
      headerName: `${t(messages.BackURL())}`,
    },
    {
      field: "secondBackupUrl",
      width: 200,
      headerName: `${t(messages.SecondBackURL())}`,
    },
    {
      field: "provinces",
      width: 100,
      headerName: `${t(messages.Provinces())}`,
      renderCell: (params: GridRenderCellParams<FlyerScraperSource>) => {
        const provinces = params.row.provinces.join(",");
        return <div>{provinces}</div>;
      },
    },
    {
      field: "scrapeTime",
      width: 250,
      headerName: `${t(messages.ScrapeTime())}`,
      filterable: false,
      renderCell: (params: GridRenderCellParams<FlyerScraperSource>) => {
        const scrapeTime = params.row.scrapeTimes
          ?.map((val) => `${val.day}-${val.time}`)
          ?.join(",");
        return <div>{scrapeTime}</div>;
      },
    },
    {
      field: "flyerTag",
      width: 250,
      headerName: `${t(messages.Flyer_Tag())}`,
      renderCell: (params: GridRenderCellParams<FlyerScraperSource>) => {
        const flyerTag = params.row.flyerTag?.map((val) => val.name)?.join(",");
        return <div>{flyerTag}</div>;
      },
    },
    {
      field: "aiScrapeEnabled",
      type: "boolean",
      width: 110,
      headerName: `${t(messages.AI_Scrape())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<FlyerScraperSource>) => {
        const enabled = !!params.row.aiScrapeEnabled;
        return (
          <div
            style={{
              color: enabled ? "#2e7d32" : "#9e9e9e",
              fontWeight: enabled ? 600 : 400,
            }}
          >
            {enabled ? t(messages.Enabled()) : t(messages.Disabled())}
          </div>
        );
      },
    },
    {
      field: "scrapePromptConfigOverride",
      type: "boolean",
      width: 100,
      headerName: `${t(messages.Override())}`,
      sortable: false,
      valueGetter: (params: GridRenderCellParams<FlyerScraperSource>) => {
        const override = params.row.scrapePromptConfigOverride;
        return (
          !!override &&
          typeof override === "object" &&
          Object.keys(override).length > 0
        );
      },
      renderCell: (params: GridRenderCellParams<FlyerScraperSource>) => {
        const hasOverride = !!params.value;
        return <div>{hasOverride ? t(messages.Yes()) : "—"}</div>;
      },
    },
    {
      field: "scrapeMode",
      width: 130,
      headerName: `${t(messages.Scrape_Mode())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<FlyerScraperSource>) => {
        const mode = params.row.scrapeMode;
        return <div>{mode ?? "—"}</div>;
      },
    },
    ...values.map((value) => {
      return {
        width: 200,
        field: value,
        headerName: `${t(messages[value]())}`,
        type: "dateTime",
        valueGetter: (val: GridRenderCellParams) => {
          return val && new Date(val?.row[value]);
        },
      };
    }),
    {
      field: "CreatedBy",
      width: 200,
      headerName: `${t(messages["CreatedBy"]())}`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<FlyerScraperSource>) => {
        const { firstName, lastName, userName, email } =
          params?.row?.adminUser || {};
        const createdBy = firstName
          ? `${firstName} ${lastName || ""}`
          : userName || email || "";
        return <div title={createdBy.trim()}>{createdBy}</div>;
      },
    },
    {
      field: "id",
      width: 300,
      headerName: `${t(messages["id"]())}`,
      filterable: false,
    },
  ];

  return columns;
};
